import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { getPortalStudents, getPortalProgressReport } from "../../lib/api";

const now = new Date();
const M = now.getMonth() + 1;
const Y = now.getFullYear();
const CURRENT_SY = M >= 7 ? `${Y}-${Y + 1}` : `${Y - 1}-${Y}`;
const PREV_SY = M >= 7 ? `${Y - 1}-${Y}` : `${Y - 2}-${Y - 1}`;
const DEFAULT_SEMESTER = M >= 8 ? "Fall" : M <= 6 ? "Spring" : "Summer";

export default function PortalProgressReports() {
  const [students, setStudents] = useState([]);
  const [semester, setSemester] = useState(DEFAULT_SEMESTER);
  const [schoolYear, setSchoolYear] = useState(CURRENT_SY);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    getPortalStudents().then((r) => setStudents(r.data || [])).catch(() => toast.error("Failed to load students"));
  }, []);

  const download = async (s) => {
    setDownloading(s.id);
    try {
      const res = await getPortalProgressReport(s.id, semester, schoolYear);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Progress_Report_${s.name.replace(/\s+/g, "_")}_${semester}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${s.name}'s report`);
    } catch (err) {
      toast.error("Could not generate that report");
    } finally { setDownloading(null); }
  };

  return (
    <div className="space-y-4" data-testid="portal-progress">
      <Card>
        <CardContent className="py-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Semester</label>
            <select value={semester} onChange={(e) => setSemester(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="portal-progress-semester">
              <option value="Fall">Fall</option>
              <option value="Spring">Spring</option>
              <option value="Summer">Summer</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">School Year</label>
            <select value={schoolYear} onChange={(e) => setSchoolYear(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="portal-progress-year">
              <option value={CURRENT_SY}>{CURRENT_SY}</option>
              <option value={PREV_SY}>{PREV_SY}</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {students.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="portal-progress-empty">No students found for this district.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="portal-progress-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left">
                  <th className="p-3">Student</th>
                  <th className="p-3">Student #</th>
                  <th className="p-3 text-right">Progress Report</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id} className="border-b hover:bg-slate-50">
                    <td className="p-3 font-medium">{s.name}</td>
                    <td className="p-3 text-muted-foreground">{s.student_number || "—"}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" disabled={downloading === s.id} onClick={() => download(s)} data-testid={`portal-progress-dl-${s.id}`}>
                        {downloading === s.id ? "Generating…" : "Download PDF"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
